import type { ProjectContentMatch, ProjectSearchContentsResult } from "@cmmd-forge/contracts";

export type ProjectContentSearchEmptyState = "disconnected" | "empty-query" | "no-results" | null;

export type ProjectContentSearchStatus =
  | { readonly kind: "hidden" }
  | { readonly kind: "loading" }
  | { readonly kind: "error"; readonly message: string }
  | { readonly kind: "invalid-regex"; readonly message: string }
  | {
      readonly kind: "summary";
      readonly fileCount: number;
      readonly resultCount: number;
      readonly truncated: boolean;
    };

export interface ProjectContentSearchPresentation {
  readonly canOpenMatches: boolean;
  readonly emptyState: ProjectContentSearchEmptyState;
  readonly matches: ReadonlyArray<ProjectContentMatch>;
  readonly status: ProjectContentSearchStatus;
}

export function createProjectContentSearchPresentation(input: {
  readonly connected: boolean;
  readonly data: ProjectSearchContentsResult | undefined;
  readonly debouncedQuery: string;
  readonly debouncePending: boolean;
  readonly error: Error | null;
  readonly isFetching: boolean;
  readonly query: string;
}): ProjectContentSearchPresentation {
  const matches = input.data?.matches ?? [];
  const querySettled = input.query === input.debouncedQuery && !input.debouncePending;
  const canOpenMatches =
    input.connected &&
    input.query.length > 0 &&
    querySettled &&
    !input.isFetching &&
    input.data !== undefined;
  const isSearching =
    input.connected &&
    input.query.length > 0 &&
    (input.debouncePending || (input.debouncedQuery.length > 0 && input.isFetching));
  const status = resolveProjectContentSearchStatus({
    data: input.data,
    error: input.error,
    isSearching,
    query: input.query,
  });
  const emptyState = !input.connected
    ? "disconnected"
    : input.query.length === 0
      ? "empty-query"
      : canOpenMatches && matches.length === 0
        ? "no-results"
        : null;
  return { canOpenMatches, emptyState, matches, status };
}

function resolveProjectContentSearchStatus(input: {
  readonly data: ProjectSearchContentsResult | undefined;
  readonly error: Error | null;
  readonly isSearching: boolean;
  readonly query: string;
}): ProjectContentSearchStatus {
  if (input.query.length === 0) return { kind: "hidden" };
  if (input.isSearching) return { kind: "loading" };
  if (input.error) return { kind: "error", message: input.error.message };
  if (input.data?.regexFallbackError) {
    return { kind: "invalid-regex", message: input.data.regexFallbackError };
  }
  if (!input.data) return { kind: "hidden" };
  return {
    kind: "summary",
    fileCount: new Set(input.data.matches.map((match) => match.path)).size,
    resultCount: input.data.matches.length,
    truncated: input.data.truncated,
  };
}

export function clampProjectContentSearchSelection(index: number, matchCount: number): number {
  return Math.min(index, Math.max(matchCount - 1, 0));
}

export function nextProjectContentSearchSelection(
  index: number,
  matchCount: number,
): number | null {
  if (matchCount === 0) return null;
  return (clampProjectContentSearchSelection(index, matchCount) + 1) % matchCount;
}

export function previousProjectContentSearchSelection(
  index: number,
  matchCount: number,
): number | null {
  if (matchCount === 0) return null;
  return (clampProjectContentSearchSelection(index, matchCount) - 1 + matchCount) % matchCount;
}
